import { useContext, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
} from "@mui/material";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PageLayout from "../../Components/UI/PageLayout.js";
import LoadingSpinner from "../../Components/UI/LoadingSpinner.js";
import { LoginContext } from "../../Components/Login/LoginContext";
import {
  GET_COMPETITIONS,
  COMPETITIONS_WITH_PERMISSION,
} from "../../GraphQL/Competitions.js";
import { GET_TEAMS, TEAMS_WITH_PERMISSION } from "../../GraphQL/Teams.js";

const DashboardPage = () => {
  const [auth] = useContext(LoginContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.loggedIn) {
      navigate("/");
    }
  }, [auth.loggedIn, navigate]);

  const competitions = useQuery(GET_COMPETITIONS, { skip: !auth.loggedIn });
  const competitionsWithPermission = useQuery(COMPETITIONS_WITH_PERMISSION, {
    skip: !auth.loggedIn,
  });
  const teams = useQuery(GET_TEAMS, { skip: !auth.loggedIn });
  const teamsWithPermission = useQuery(TEAMS_WITH_PERMISSION, {
    skip: !auth.loggedIn,
  });

  if (!auth.loggedIn) return null;

  if (
    competitions.loading ||
    competitionsWithPermission.loading ||
    teams.loading ||
    teamsWithPermission.loading
  )
    return <LoadingSpinner />;

  const myCompetitionIds = competitionsWithPermission.data
    ? competitionsWithPermission.data.competitionsWithPermission.map((c) =>
        String(c.id)
      )
    : [];
  const myCompetitions = competitions.data
    ? competitions.data.competitions.filter((c) =>
        myCompetitionIds.includes(String(c.id))
      )
    : [];

  const myTeamIds = teamsWithPermission.data
    ? teamsWithPermission.data.teamsWithPermission.map((t) => String(t.id))
    : [];
  const myTeams = teams.data
    ? teams.data.teams.filter((t) => myTeamIds.includes(String(t.id)))
    : [];

  const hasItems = myCompetitions.length > 0 || myTeams.length > 0;

  return (
    <PageLayout
      title="Dashboard"
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Dashboard" }]}
    >
      {!hasItems && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            Nothing here yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You don't manage any competitions or teams yet. Get started by
            registering as a manager or joining a competition.
          </Typography>
        </Box>
      )}

      {myCompetitions.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <EmojiEventsRoundedIcon sx={{ color: "primary.main" }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              My Competitions
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {myCompetitions.map((comp) => (
              <Grid item xs={12} sm={4} key={comp.id}>
                <Card sx={{ backgroundColor: "#2a3447" }}>
                  <CardActionArea
                    component={Link}
                    to={`/competition/${comp.id}`}
                  >
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {comp.name}
                      </Typography>
                      {comp.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          {comp.description}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {myTeams.length > 0 && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <GroupsRoundedIcon sx={{ color: "primary.main" }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              My Teams
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {myTeams.map((team) => (
              <Grid item xs={12} sm={4} key={team.id}>
                <Card sx={{ backgroundColor: "#2a3447" }}>
                  <CardActionArea component={Link} to={`/team/${team.id}`}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {team.name}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </PageLayout>
  );
};

export default DashboardPage;
